/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { RefObject } from 'react';
import { useMediaQuery } from '@mui/material';
import { CloseIcon } from '../../icons';
import { MOBILE_BREAKPOINT_THRESHOLD, styled } from '../../theme';
import { Typography } from '../typography';
import { Button } from '../button';
import { CloseThinIcon } from '../../icons/closeThin';
import { useTheme } from '../../hooks/useTheme';
import { Box } from '../box';
import { mergeSx } from '../../utils/sx/merge';
import { ChevronRightIcon } from '../../icons/chevronRight';
import { Stack } from '../stack';
import { SVGIconTokens } from '../svgIcon';
import { ModalActions } from './components/actions';
import { AppModalAction } from './components/actions/model';
import { Puller } from './components/puller';
import { MODAL_LAYOUT_HORIZONTAL_SPACING_DESKTOP, MODAL_MOBILE_BREAKPOINT_THRESHOLD, Tokens, name } from './const';
import { ModalLayoutProps } from './model';
import { useModalLayoutContext } from './context';
import { HeaderIcon } from './components/headerIcon';
import { ModalContainer } from './container';

const FullscreenRoot = styled(Box, { name })(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
}));

const Root = styled(Box, { name })(({ theme }) => ({
  [Tokens.Radius]: `${theme.borderRadius.large}px`,
  [Tokens.PullerColor]: theme.palette['gray light'],
  [Tokens.HeaderIconTop]: '-2.8rem',
  position: 'relative',
  maxHeight: '100%',
  borderTopLeftRadius: Tokens.Radius(),
  borderTopRightRadius: Tokens.Radius(),
  backgroundColor: theme.palette.background.paper,
  paddingTop: Tokens.PaddingTop(),
  paddingBottom: Tokens.PaddingBottom(theme.spacing(2.5)),

  [theme.breakpoints.up(MODAL_MOBILE_BREAKPOINT_THRESHOLD)]: {
    [Tokens.Radius]: `${theme.borderRadius.extraLarge}px`,
    [Tokens.PaddingTop]: theme.spacing(7.25),
    [Tokens.HeaderIconTop]: '-3rem',
    borderBottomLeftRadius: Tokens.Radius(),
    borderBottomRightRadius: Tokens.Radius(),
    paddingLeft: theme.spacing(MODAL_LAYOUT_HORIZONTAL_SPACING_DESKTOP),
    paddingRight: theme.spacing(MODAL_LAYOUT_HORIZONTAL_SPACING_DESKTOP),
    paddingBottom: Tokens.PaddingBottom(theme.spacing(7.25)),
  },
}));

export const ModalLayout = React.forwardRef<HTMLDivElement, React.PropsWithChildren<ModalLayoutProps>>(
  (
    {
      title,
      icon,
      titleProps: _titleProps,
      footer,
      swipable = true,
      isMobile: isMobileFromProps,
      containerSx,
      message,
      actions,
      onClose,
      onBack,
      children,
      id,
      swipeAreaRef: swipableAreaRef,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isMobileFromMediaQuery = useMediaQuery(theme.breakpoints.down(MODAL_MOBILE_BREAKPOINT_THRESHOLD));
    const { isFullscreen } = useModalLayoutContext();
    const { sx: titlePropsSx, ...titleProps } = _titleProps ?? {};

    const isMobile = isMobileFromProps ?? isMobileFromMediaQuery;

    if (isFullscreen) {
      return (
        <FullscreenRoot
          id={id}
          ref={ref}
          role="presentation"
          sx={mergeSx(
            {
              pt: Boolean(onBack) && isMobile ? 0 : 2.5,
            },
            containerSx,
          )}
        >
          <ModalContainer>
            {Boolean(title || onBack) && (
              <Typography
                component="div"
                sx={mergeSx({ textAlign: 'center', pt: 2.5, pb: 2.725 }, titlePropsSx)}
                variant={isMobile ? 'h2' : 'h1'}
                {...titleProps}
              >
                {Boolean(onBack) && isMobile ? (
                  <Stack
                    direction="row"
                    sx={{
                      typography: 'h2',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 4,
                      position: 'relative',
                    }}
                  >
                    <Button
                      onClick={onBack}
                      sx={{
                        position: 'absolute',
                        zIndex: 10,
                        left: 0,
                        transform: 'rotate(180deg)',
                        [SVGIconTokens.Size]: '1.75rem',
                      }}
                      variant="text"
                    >
                      <ChevronRightIcon />
                    </Button>
                    {Boolean(title) && <div>{title}</div>}
                  </Stack>
                ) : (
                  title
                )}
              </Typography>
            )}
          </ModalContainer>
          {Boolean(onClose) && (
            <Button
              color="white"
              onClick={onClose}
              size="small"
              square
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                backgroundColor: { xs: 'transparent !important', [MOBILE_BREAKPOINT_THRESHOLD]: undefined },
                [SVGIconTokens.Size]: { xs: '2rem', [MODAL_MOBILE_BREAKPOINT_THRESHOLD]: '1.75rem' },
              }}
              variant="primary"
            >
              {isMobile ? <CloseIcon /> : <CloseThinIcon />}
            </Button>
          )}
          {children}
        </FullscreenRoot>
      );
    }

    return (
      <Root
        id={id}
        ref={ref}
        role="presentation"
        sx={mergeSx(
          {
            ...(icon && { [Tokens.PaddingTop]: theme.spacing(isMobile ? 4.25 : 10) }),
          },
          containerSx,
        )}
      >
        {swipable && isMobile && !icon && <Puller ref={swipableAreaRef as RefObject<HTMLDivElement>} />}
        {Boolean(onClose || onBack) && !isMobile && (
          <Button
            color="white"
            onClick={onClose || onBack}
            size="small"
            square
            sx={{ position: 'absolute', top: theme.spacing(1), right: theme.spacing(1) }}
            variant="primary"
          >
            <CloseThinIcon sx={{ fontSize: '2rem' }} />
          </Button>
        )}
        {Boolean(title || onClose || onBack || icon) && (
          <ModalContainer sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
            {icon && <HeaderIcon>{icon}</HeaderIcon>}
            {Boolean(title) && (
              <Typography
                sx={{
                  textAlign: 'center',
                  flexGrow: 1,
                  overflow: 'hidden',
                  pb: isMobile ? undefined : 3.5,
                  pr: isMobile ? 1 : undefined,
                  py: isMobile ? 2 : undefined,
                  mt: icon ? 2 : 0,
                  ...titlePropsSx,
                }}
                variant={isMobile ? 'h5' : 'h4'}
                {...titleProps}
              >
                {title}
              </Typography>
            )}
          </ModalContainer>
        )}

        {Boolean(message) && <Typography variant="body">{message}</Typography>}

        {children}

        {Boolean(actions) && <ModalActions actions={actions as AppModalAction[]} />}

        {footer}
      </Root>
    );
  },
);
ModalLayout.displayName = 'ModalLayout';
